import type { CheckIn } from "../../../domain/check-in/check-in";
import type { DiaryEntry } from "../../../domain/diary/diary-entry";
import type { Food } from "../../../domain/foods/food";
import type { GoalPhase } from "../../../domain/goals/goal";
import type { Recipe } from "../../../domain/recipes/recipe";
import type { NutrientProfile } from "../../../domain/nutrition/nutrients";
import { normalizeKnownMicronutrients } from "../../../domain/nutrition/nutrient-normalization";
import type { DailyNutrientTarget } from "../../../domain/nutrition/nutrient-target";
import type { WeekPlan } from "../../../domain/planning/week-plan";
import type { WeightEntry } from "../../../domain/weight/weight-entry";
import type { OnboardingWeightReference } from "../../../domain/weight/onboarding-reference";
import type {
  CheckInRecord,
  DiaryEntryRecord,
  FoodRecord,
  GoalPhaseRecord,
  NutritionSnapshotRecord,
  NutrientGoalRecord,
  OnboardingWeightReferenceRecord,
  RecipeRecord,
  WeekPlanRecord,
  WeightEntryRecord,
} from "../records";

export function foodToRecord(food: Food): FoodRecord {
  return {
    id: food.id,
    name: food.name,
    brand: food.brand,
    barcode: food.barcode,
    basis: { ...food.basis },
    ...(food.measures === undefined ? {} : { measures: food.measures.map((measure) => ({ ...measure })) }),
    nutrition: nutrientsToRecord(food.nutrients),
    source: {
      provider: food.provenance.kind === "imported"
        ? (food.provenance.provider ?? "imported")
        : food.provenance.kind,
      externalId: food.provenance.kind === "imported" ? food.provenance.providerFoodId : undefined,
      importedAt: food.provenance.kind === "imported" ? food.provenance.importedAt : undefined,
    },
    isPreferred: food.preferred,
    createdAt: food.createdAt,
    updatedAt: food.updatedAt,
  };
}

export function foodFromRecord(record: FoodRecord): Food {
  return {
    id: record.id,
    name: record.name,
    brand: record.brand,
    barcode: record.barcode,
    basis: { ...record.basis },
    ...(record.measures === undefined ? {} : { measures: record.measures.map((measure) => ({ ...measure })) }),
    nutrients: nutrientsFromRecord(record.nutrition),
    provenance: record.source.provider === "custom"
      ? { kind: "custom" }
      : record.source.provider === "photo-estimate"
        ? { kind: "photo-estimate" }
        : {
            kind: "imported",
            provider: record.source.provider,
            providerFoodId: record.source.externalId,
            importedAt: record.source.importedAt,
          },
    preferred: record.isPreferred,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  };
}

export function recipeToRecord(recipe: Recipe): RecipeRecord {
  return {
    id: recipe.id,
    name: recipe.name,
    servingName: recipe.servingName,
    servingCount: recipe.servingCount,
    ingredients: recipe.ingredients.map((ingredient) => ({
      foodId: ingredient.foodId,
      name: ingredient.name,
      brand: ingredient.brand,
      basis: { ...ingredient.basis },
      nutrition: nutrientsToRecord(ingredient.nutrients),
      portion: { ...ingredient.portion },
    })),
    createdAt: recipe.createdAt,
    updatedAt: recipe.updatedAt,
  };
}

export function recipeFromRecord(record: RecipeRecord): Recipe {
  return {
    id: record.id,
    name: record.name,
    servingName: record.servingName,
    servingCount: record.servingCount,
    ingredients: record.ingredients.map((ingredient) => ({
      foodId: ingredient.foodId,
      name: ingredient.name,
      brand: ingredient.brand,
      basis: { ...ingredient.basis },
      nutrients: nutrientsFromRecord(ingredient.nutrition),
      portion: { ...ingredient.portion },
    })),
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  };
}

export function diaryToRecord(entry: DiaryEntry): DiaryEntryRecord {
  return {
    id: entry.id,
    date: entry.date,
    meal: entry.meal,
    food: {
      foodId: entry.food.foodId,
      name: entry.food.name,
      brand: entry.food.brand,
      barcode: entry.food.barcode,
      basis: { ...entry.food.basis },
      ...(entry.food.measures === undefined
        ? {}
        : { measures: entry.food.measures.map((measure) => ({ ...measure })) }),
      portion: {
        ...entry.food.portion,
        ...(entry.food.portion.display === undefined ? {} : { display: { ...entry.food.portion.display } }),
      },
      nutrition: nutrientsToRecord(entry.food.nutrients),
    },
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
  };
}

export function diaryFromRecord(record: DiaryEntryRecord): DiaryEntry {
  const nutrients = nutrientsFromRecord(record.food.nutrition);
  const calories = nutrients.macros.proteinGrams * 4 + nutrients.macros.carbohydrateGrams * 4 + nutrients.macros.fatGrams * 9;
  return {
    id: record.id,
    date: record.date,
    meal: record.meal,
    food: {
      foodId: record.food.foodId,
      name: record.food.name,
      brand: record.food.brand,
      barcode: record.food.barcode,
      basis: { ...record.food.basis },
      ...(record.food.measures === undefined
        ? {}
        : { measures: record.food.measures.map((measure) => ({ ...measure })) }),
      portion: {
        ...record.food.portion,
        ...(record.food.portion.display === undefined ? {} : { display: { ...record.food.portion.display } }),
      },
      nutrients,
      calories,
    },
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  };
}

export function weightToRecord(entry: WeightEntry): WeightEntryRecord {
  return {
    id: entry.id,
    date: entry.date,
    weightGrams: Math.round(entry.kilograms * 1_000),
    recordedAt: entry.recordedAt,
    updatedAt: entry.updatedAt,
  };
}

export function weightFromRecord(record: WeightEntryRecord): WeightEntry {
  return {
    id: record.id,
    date: record.date,
    kilograms: record.weightGrams / 1_000,
    recordedAt: record.recordedAt,
    updatedAt: record.updatedAt,
  };
}

export function onboardingReferenceToRecord(reference: OnboardingWeightReference): OnboardingWeightReferenceRecord {
  const sample = reference.samples[0];
  return {
    id: reference.id,
    date: reference.date,
    weightGrams: Math.round(reference.weightKilograms * 1_000),
    samples: [{ date: sample.date, weightGrams: Math.round(sample.kilograms * 1_000) }],
    trendPolicyVersion: reference.trendPolicyVersion,
    createdAt: reference.createdAt,
  };
}

export function onboardingReferenceFromRecord(record: OnboardingWeightReferenceRecord): OnboardingWeightReference {
  const sample = record.samples[0];
  return {
    id: record.id,
    date: record.date,
    weightKilograms: record.weightGrams / 1_000,
    samples: [{ date: sample.date, kilograms: sample.weightGrams / 1_000 }],
    trendPolicyVersion: record.trendPolicyVersion as OnboardingWeightReference["trendPolicyVersion"],
    createdAt: record.createdAt,
  };
}

export function goalToRecord(goal: GoalPhase): GoalPhaseRecord {
  return goal.mode === "maintain" ? {
    id: goal.id,
    mode: goal.mode,
    effectiveFrom: goal.effectiveFrom,
    targetWeightGrams: Math.round(goal.targetWeightKilograms * 1_000),
    tolerancePercent: goal.tolerancePercent,
    createdAt: goal.createdAt,
  } : {
    id: goal.id,
    mode: goal.mode,
    effectiveFrom: goal.effectiveFrom,
    weeklyRatePercent: goal.weeklyRatePercent,
    createdAt: goal.createdAt,
  };
}

export function goalFromRecord(record: GoalPhaseRecord): GoalPhase {
  if (record.mode === "maintain") {
    if (record.targetWeightGrams === undefined || record.tolerancePercent === undefined) {
      throw new Error(`Stored maintenance goal ${record.id} is incomplete`);
    }
    return {
      id: record.id,
      mode: record.mode,
      effectiveFrom: record.effectiveFrom,
      targetWeightKilograms: record.targetWeightGrams / 1_000,
      tolerancePercent: record.tolerancePercent,
      createdAt: record.createdAt,
    };
  }
  if (record.weeklyRatePercent === undefined) throw new Error(`Stored ${record.mode} goal ${record.id} is incomplete`);
  return {
    id: record.id,
    mode: record.mode,
    effectiveFrom: record.effectiveFrom,
    weeklyRatePercent: record.weeklyRatePercent,
    createdAt: record.createdAt,
  };
}

export function weekPlanToRecord(plan: WeekPlan): WeekPlanRecord {
  return {
    id: plan.id,
    startDate: plan.startDate,
    endDate: plan.endDate,
    checkInDueDate: plan.checkInDueDate,
    status: plan.status,
    baselineDailyCalories: plan.baselineDailyCalories,
    weeklyCalorieBudget: plan.weeklyCalorieBudget,
    allocations: plan.allocations.map((allocation) => ({
      date: allocation.date,
      calories: allocation.calories,
      isLocked: allocation.isLocked,
      initializedAsCatchUp: allocation.initializedAsCatchUp,
    })),
    fixedDailyProteinMg: Math.round(plan.fixedDailyProteinGrams * 1_000),
    macroDistribution: { ...plan.macroDistribution },
    dailyFloorPolicy: { ...plan.dailyFloorPolicy },
    recommendationPolicyVersion: plan.recommendationPolicyVersion,
    goalPhaseId: plan.goalPhaseId,
    createdAt: plan.createdAt,
    completedAt: plan.completedAt,
  };
}

export function weekPlanFromRecord(record: WeekPlanRecord): WeekPlan {
  return {
    id: record.id,
    startDate: record.startDate,
    endDate: record.endDate,
    checkInDueDate: record.checkInDueDate,
    status: record.status,
    baselineDailyCalories: record.baselineDailyCalories,
    weeklyCalorieBudget: record.weeklyCalorieBudget,
    allocations: record.allocations.map((allocation) => ({
      date: allocation.date,
      calories: allocation.calories,
      isLocked: allocationLockFromRecord(allocation),
      initializedAsCatchUp: allocation.initializedAsCatchUp,
    })),
    fixedDailyProteinGrams: record.fixedDailyProteinMg / 1_000,
    macroDistribution: { ...record.macroDistribution },
    dailyFloorPolicy: { ...record.dailyFloorPolicy },
    recommendationPolicyVersion: record.recommendationPolicyVersion,
    goalPhaseId: record.goalPhaseId,
    createdAt: record.createdAt,
    completedAt: record.completedAt,
  };
}

function allocationLockFromRecord(allocation: WeekPlanRecord["allocations"][number]): boolean {
  const value: unknown = allocation.isLocked;
  if (value === undefined) return false;
  if (typeof value !== "boolean") {
    throw new Error(`Stored week allocation ${allocation.date} has an invalid lock state`);
  }
  return value;
}

export function checkInToRecord(checkIn: CheckIn): CheckInRecord {
  return {
    id: checkIn.id,
    weekPlanId: checkIn.weekPlanId,
    dueDate: checkIn.dueDate,
    completedDate: checkIn.completedDate,
    completedAt: checkIn.completedAt,
    questionnaire: { ...checkIn.questionnaire },
    recommendation: { ...checkIn.recommendation },
    acceptedBaselineCalories: checkIn.acceptedBaselineCalories,
    baselineWasOverridden: checkIn.baselineWasOverridden,
    previousTrend: checkIn.previousTrend ? trendToRecord(checkIn.previousTrend) : undefined,
    currentTrend: checkIn.currentTrend ? trendToRecord(checkIn.currentTrend) : undefined,
    proteinDecision: {
      policyVersion: checkIn.proteinDecision.policyVersion,
      proposedDailyGrams: checkIn.proteinDecision.proposedDailyGrams,
      acceptedDailyGrams: checkIn.proteinDecision.acceptedDailyGrams,
      wasOverridden: checkIn.proteinDecision.wasOverridden,
      gramsPerKilogram: checkIn.proteinDecision.gramsPerKilogram,
      referenceWeightGrams: checkIn.proteinDecision.referenceWeightKilograms === undefined
        ? undefined
        : Math.round(checkIn.proteinDecision.referenceWeightKilograms * 1_000),
      referenceDate: checkIn.proteinDecision.referenceDate,
      source: checkIn.proteinDecision.source,
    },
    nextWeekPlanId: checkIn.nextWeekPlanId,
  };
}

export function checkInFromRecord(record: CheckInRecord): CheckIn {
  return {
    id: record.id,
    weekPlanId: record.weekPlanId,
    dueDate: record.dueDate,
    completedDate: record.completedDate,
    completedAt: record.completedAt,
    questionnaire: { ...record.questionnaire },
    recommendation: {
      ...record.recommendation,
      reason: record.recommendation.reason as CheckIn["recommendation"]["reason"],
    },
    acceptedBaselineCalories: record.acceptedBaselineCalories,
    baselineWasOverridden: record.baselineWasOverridden,
    previousTrend: record.previousTrend ? trendFromRecord(record.previousTrend) : undefined,
    currentTrend: record.currentTrend ? trendFromRecord(record.currentTrend) : undefined,
    proteinDecision: {
      policyVersion: record.proteinDecision.policyVersion,
      proposedDailyGrams: record.proteinDecision.proposedDailyGrams,
      acceptedDailyGrams: record.proteinDecision.acceptedDailyGrams,
      wasOverridden: record.proteinDecision.wasOverridden,
      gramsPerKilogram: record.proteinDecision.gramsPerKilogram,
      referenceWeightKilograms: record.proteinDecision.referenceWeightGrams === undefined
        ? undefined
        : record.proteinDecision.referenceWeightGrams / 1_000,
      referenceDate: record.proteinDecision.referenceDate,
      source: record.proteinDecision.source,
    },
    nextWeekPlanId: record.nextWeekPlanId,
  };
}

export function nutrientTargetToRecord(target: DailyNutrientTarget): NutrientGoalRecord {
  return {
    id: target.nutrientKey,
    nutrientKey: target.nutrientKey,
    amount: target.amount,
    unit: target.unit,
    name: target.displayName,
  };
}

export function nutrientTargetFromRecord(record: NutrientGoalRecord): DailyNutrientTarget {
  return { nutrientKey: record.nutrientKey, displayName: record.name, amount: record.amount, unit: record.unit };
}

export function nutrientsToRecord(nutrients: NutrientProfile): NutritionSnapshotRecord {
  const micronutrients = normalizeKnownMicronutrients(nutrients.micronutrients);
  return {
    proteinMg: Math.round(nutrients.macros.proteinGrams * 1_000),
    carbohydrateMg: Math.round(nutrients.macros.carbohydrateGrams * 1_000),
    fatMg: Math.round(nutrients.macros.fatGrams * 1_000),
    fibreMg: nutrients.fibreGrams === undefined ? undefined : Math.round(nutrients.fibreGrams * 1_000),
    micronutrients: Object.fromEntries(Object.entries(micronutrients).map(([key, value]) => [key, { ...value }])),
  };
}

export function nutrientsFromRecord(record: NutritionSnapshotRecord): NutrientProfile {
  return {
    macros: {
      proteinGrams: record.proteinMg / 1_000,
      carbohydrateGrams: record.carbohydrateMg / 1_000,
      fatGrams: record.fatMg / 1_000,
    },
    fibreGrams: record.fibreMg === undefined ? undefined : record.fibreMg / 1_000,
    micronutrients: normalizeKnownMicronutrients(record.micronutrients),
  };
}

function trendToRecord(trend: NonNullable<CheckIn["currentTrend"]>): NonNullable<CheckInRecord["currentTrend"]> {
  return {
    policyVersion: trend.policyVersion,
    source: trend.source,
    date: trend.date,
    averageWeightGrams: Math.round(trend.averageKilograms * 1_000),
    samples: trend.samples.map((sample) => ({ date: sample.date, weightGrams: Math.round(sample.kilograms * 1_000) })),
  };
}

function trendFromRecord(trend: NonNullable<CheckInRecord["currentTrend"]>): NonNullable<CheckIn["currentTrend"]> {
  return {
    policyVersion: trend.policyVersion,
    source: trend.source,
    date: trend.date,
    averageKilograms: trend.averageWeightGrams / 1_000,
    samples: trend.samples.map((sample) => ({ date: sample.date, kilograms: sample.weightGrams / 1_000 })),
  };
}
